import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { SupabaseCustomEmailDomainService } from "../../server/supabase-custom-email-domain-service";
import { customEmailDomainSchema } from "../../types";

import type { CustomEmailDomain } from "../../server/supabase-custom-email-domain-service";
import type { FC } from "react";

export const AddCustomEmailDomain: FC<{
  onAdd: (domain: CustomEmailDomain) => void;
}> = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { handleSubmit, control } = useForm({
    defaultValues: { domain: "" },
    resolver: yupResolver(customEmailDomainSchema),
  });

  const onSubmit = useSupabaseCallback(
    async ({ supabase }, values: { domain: string }): Promise<void> => {
      setSaving(true);

      const service = new SupabaseCustomEmailDomainService(supabase);

      const { data, error } = await service.insert({ cname_records: [], domain: values.domain });

      setSaving(false);

      if (error || !data) {
        toast.error(`Something went wrong when adding ${values.domain}`);
        return;
      }

      toast.success(`${values.domain} added successfully.`);
      onAdd(data);
      setOpen(false);
    },
    [onAdd],
  );

  const handleClickOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    // Prevent closing the dialog if we're saving
    if (!saving) {
      setOpen(false);
    }
  };

  return (
    <form id="create-domain-form" onSubmit={handleSubmit(onSubmit)}>
      <Button
        data-analytics-id="email-domain-table-add-domain"
        color="primary"
        size="large"
        sx={{ mt: 3, mb: 4 }}
        variant="contained"
        onClick={handleClickOpen}
      >
        Add Domain
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Domain</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Verify an entire domain. When you verify a new domain, we provide a set of DNS records.
            You have to add these records to the DNS configuration of the domain.
          </DialogContentText>
          <Controller
            name="domain"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                margin="dense"
                id="domain"
                label="Domain"
                type="text"
                fullWidth
                variant="standard"
                value={field.value}
                placeholder="example.com"
                onChange={field.onChange}
                helperText={error?.message}
                error={Boolean(error)}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="email-domain-add-domain-dialog-add"
            disabled={saving}
            type="submit"
            form="create-domain-form"
          >
            {saving ? <CircularProgress size={20} /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
};
